import React, { useState, useEffect } from 'react';
import './App.css';
import {
  ArrowRight,
  Lock,
  Check,
  X,
  CalendarDays,
  Zap,
  Clock,
  Sun,
  Moon,
  Briefcase,
  BellRing,
  Layers,
  Activity,
  Menu,
  ShieldCheck,
  CheckCircle2,
  HelpCircle
} from 'lucide-react';

function App() {
  const [isDarkMode, setIsDarkMode] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    const currentHour = new Date().getHours();
    if (currentHour >= 5 && currentHour < 17) {
      setIsDarkMode(false);
    } else {
      setIsDarkMode(true);
    }
  }, []);

  const APP_URL = "https://app.ledgerplanner.com";
  const DEMO_URL = "https://demo.ledgerplanner.com";
  const PLAN_MONTHLY_URL = "https://app.ledgerplanner.com/signup?plan=monthly";
  const PLAN_ANNUAL_URL = "https://app.ledgerplanner.com/signup?plan=annual";
  const PLAN_FOUNDER_URL = "https://app.ledgerplanner.com/signup?plan=founder";

  return (
    <div className={`showroom-container ${isDarkMode ? 'dark-mode' : 'light-mode'}`}>
      
      {/* AMBIENT AURORA BACKDROPS */}
      <div className="ambient-aurora aurora-blue"></div>
      <div className="ambient-aurora aurora-green"></div>

      {/* SINGLE DYNAMIC HORIZON GROWTH TRAJECTORY (FULL 60% BAND STRETCH, 12S PACING) */}
      <div className="runway-stage-layer" aria-hidden="true">
        <div className="growth-chart-stream-centered">
          <svg className="growth-chart-svg" viewBox="0 0 1200 600" fill="none" preserveAspectRatio="none">
            {/* Extended vertical coordinate scale across full 600-unit height */}
            <path
              d="M0,540 L140,510 L260,360 L390,420 L530,250 L680,320 L840,140 L990,190 L1200,30"
              stroke="url(#growthGradientBlueGreen)"
              strokeWidth="4"
              strokeLinecap="round"
              strokeLinejoin="round"
              className="growth-path-stroke"
            />

            {/* Node 1: Initial Baseline Dip (Brand Blue) */}
            <circle cx="140" cy="510" r="7" fill="#1877F2" filter="drop-shadow(0 0 10px #1877F2)" className="trajectory-dot dot-1" />

            {/* Node 2: First Payday Peak (Inflow Green) */}
            <circle cx="260" cy="360" r="8" fill="#10B981" filter="drop-shadow(0 0 12px #10B981)" className="trajectory-dot dot-2" />

            {/* Node 3: Fixed Obligation Trough (Brand Blue) */}
            <circle cx="390" cy="420" r="7" fill="#1877F2" filter="drop-shadow(0 0 10px #1877F2)" className="trajectory-dot dot-3" />

            {/* Node 4: Secondary Paycheck Surge (Inflow Green) */}
            <circle cx="530" cy="250" r="8" fill="#10B981" filter="drop-shadow(0 0 12px #10B981)" className="trajectory-dot dot-4" />

            {/* Node 5: Recurring Bill Checkpoint (Brand Blue) */}
            <circle cx="680" cy="320" r="7" fill="#1877F2" filter="drop-shadow(0 0 10px #1877F2)" className="trajectory-dot dot-5" />

            {/* Node 6: Capital Reserve Accumulation (Inflow Green) */}
            <circle cx="840" cy="140" r="9" fill="#10B981" filter="drop-shadow(0 0 14px #10B981)" className="trajectory-dot dot-6" />

            {/* Node 7: Variable Expense Buffer (Brand Blue) */}
            <circle cx="990" cy="190" r="7" fill="#1877F2" filter="drop-shadow(0 0 10px #1877F2)" className="trajectory-dot dot-7" />

            {/* Node 8: Apex Runway Freedom (Inflow Green) */}
            <circle cx="1200" cy="30" r="10" fill="#10B981" filter="drop-shadow(0 0 16px #10B981)" className="trajectory-dot dot-8" />

            <defs>
              <linearGradient id="growthGradientBlueGreen" x1="0%" y1="0%" x2="100%" y2="0%">
                <stop offset="0%" stopColor="#1877F2" stopOpacity="0.25" />
                <stop offset="25%" stopColor="#1877F2" stopOpacity="0.85" />
                <stop offset="55%" stopColor="#10B981" stopOpacity="0.9" />
                <stop offset="80%" stopColor="#1877F2" stopOpacity="0.85" />
                <stop offset="100%" stopColor="#10B981" stopOpacity="1" />
              </linearGradient>
            </defs>
          </svg>
        </div>
      </div>

      {/* 1. ALWAYS-STICKY RESPONSIVE NAVIGATION */}
      <nav className="showroom-nav">
        
        {/* Left Section: Brand + Links + Theme Switcher */}
        <div className="nav-left-group">
          <div className="nav-brand">
            <img 
              src="/login-logo.png" 
              alt="Ledger Planner" 
              className="brand-logo-img"
            />
            <span className="brand-title-text">Ledger Planner</span>
          </div>

          <div className="nav-links-cluster">
            <a href="#how-it-works" className="nav-link">How it works</a>
            <a href="#features" className="nav-link">Features</a>
            <a href="#faq" className="nav-link">FAQ</a>
            
            <button 
              onClick={() => setIsDarkMode(!isDarkMode)} 
              className="theme-toggle-btn"
              title={isDarkMode ? "Switch to Daytime Mode" : "Switch to Evening Vault Mode"}
              aria-label="Toggle visual theme"
            >
              {isDarkMode ? <Sun size={17} /> : <Moon size={17} />}
            </button>
          </div>
        </div>

        {/* Right Section: Action Cluster (Login, Demo, Start Free Trial) */}
        <div className="nav-right-actions">
          <a href={APP_URL} className="nav-login-link">Login</a>
          <a href={DEMO_URL} className="btn-nav-demo-orange">Demo</a>
          <a href={PLAN_ANNUAL_URL} className="btn-nav-primary-blue">Start Free Trial</a>
        </div>

        {/* Mobile Navigation Trigger & Controls */}
        <div className="nav-mobile-trigger">
          <button 
            onClick={() => setIsDarkMode(!isDarkMode)} 
            className="theme-toggle-btn"
            aria-label="Toggle theme"
          >
            {isDarkMode ? <Sun size={18} /> : <Moon size={18} />}
          </button>
          
          <a 
            href={PLAN_ANNUAL_URL} 
            className="btn-nav-primary-blue" 
            style={{ padding: '0.45rem 0.85rem', fontSize: '0.82rem' }}
          >
            Start Free
          </a>

          <button 
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="theme-toggle-btn"
            aria-label="Toggle menu"
          >
            {mobileMenuOpen ? <X size={20} /> : <Menu size={20} />}
          </button>
        </div>

        {/* Mobile Dropdown Drawer */}
        <div className={`mobile-menu-drawer ${mobileMenuOpen ? 'open' : ''}`}>
          <a href={DEMO_URL} className="mobile-menu-link" onClick={() => setMobileMenuOpen(false)}>
            <span>Explore Interactive Demo</span>
            <ArrowRight size={16} />
          </a>
          <a href={APP_URL} className="mobile-menu-link" onClick={() => setMobileMenuOpen(false)}>
            <span>Login to Ledger Planner</span>
            <ArrowRight size={16} />
          </a>
          <a href="#how-it-works" className="mobile-menu-link" onClick={() => setMobileMenuOpen(false)}>
            <span>How It Works</span>
            <ArrowRight size={16} />
          </a>
          <a href="#features" className="mobile-menu-link" onClick={() => setMobileMenuOpen(false)}>
            <span>Features</span>
            <ArrowRight size={16} />
          </a>
          <a href="#faq" className="mobile-menu-link" onClick={() => setMobileMenuOpen(false)}>
            <span>FAQ</span>
            <ArrowRight size={16} />
          </a>
          <a href="#pricing" className="mobile-menu-link" onClick={() => setMobileMenuOpen(false)}>
            <span>View Pricing Plans</span>
            <ArrowRight size={16} />
          </a>
        </div>
      </nav>

      {/* 2. HERO SECTION */}
      <header className="hero-section">
        <div className="trust-pill">
          <span className="live-indicator"></span>
          <span>Zero Bank Logins • 100% Private Cash Vault</span>
        </div>

        <h1 className="hero-headline">
          Stop Guessing What’s Safe to Spend.<br />
          <span className="highlight-blue">Total Payday</span> <span className="highlight-green">Peace of Mind.</span>
        </h1>

        <p className="hero-subtext">
          Seeing $1,000 in your account doesn’t mean you have $1,000 to spend. Traditional budget apps break your bank connections and lump everything into a blind monthly bucket. Ledger Planner connects every bill to your actual paydays, giving you your exact daily spending ceiling without checking four banking apps.
        </p>

        <div className="hero-cta-group">
          <div className="hero-actions-row">
            <a href={PLAN_ANNUAL_URL} className="btn-hero-primary">
              <span>Start 14-Day Free Trial</span>
              <ArrowRight size={20} />
            </a>
            <a href={DEMO_URL} className="btn-hero-demo">
              <Activity size={18} color="#10B981" />
              <span>Explore Live Sandbox Demo</span>
            </a>
          </div>

          <div className="cta-reassurance">
            <span><Lock size={14} /> Bank credentials never requested</span>
            <span>•</span>
            <span><Clock size={14} /> 60-second setup</span>
            <span>•</span>
            <span><Check size={14} /> Cancel anytime</span>
          </div>
        </div>
      </header>

      {/* 3. CUSTOM PC WORKSTATION MONITOR SHOWCASE */}
      <section className="showcase-section">
        <div className="workstation-assembly">
          
          <div className="pc-monitor-enclosure">
            <div className="pc-monitor-screen-wrapper">
              <img 
                src="/dashboard-light.png" 
                alt="Ledger Planner Desktop Dashboard - Daytime View" 
                className="showcase-desktop-img light"
              />
              <img 
                src="/dashboard-dark.png" 
                alt="Ledger Planner Desktop Dashboard - Evening Mode" 
                className="showcase-desktop-img dark"
              />
            </div>
            <div className="pc-monitor-power-led" title="System Live"></div>
          </div>

          <div className="pc-monitor-neck"></div>
          <div className="pc-monitor-base"></div>

          <div className="showcase-badges-row">
            <a href={APP_URL} className="app-badge-btn" target="_blank" rel="noreferrer">
              <svg className="app-badge-icon" viewBox="0 0 24 24" fill="currentColor">
                <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M15.97 6.37c.62-.75 1.04-1.8 0.92-2.85-.9.04-1.99.6-2.64 1.35-.58.66-1.09 1.73-.95 2.76 1.01.08 2.05-.51 2.67-1.26z"/>
              </svg>
              <div className="app-badge-text">
                <span className="badge-eyebrow">Download on the</span>
                <span className="badge-store-title">App Store</span>
              </div>
            </a>

            <a href={APP_URL} className="app-badge-btn" target="_blank" rel="noreferrer">
              <svg className="app-badge-icon" viewBox="0 0 24 24" fill="currentColor">
                <path d="M3.609 1.814L13.792 12 3.61 22.186c-.352-.338-.61-.885-.61-1.637V3.451c0-.752.258-1.299.61-1.637zm11.233 11.234l2.548-2.548-9.988-5.766 7.44 8.314zm0 .898l-7.44 8.314 9.988-5.766-2.548-2.548zm1.05-1.049l3.52 2.032c1.002.578 1.002 1.517 0 2.095l-3.52 2.032-2.001-2.001 2.001-2.158z"/>
              </svg>
              <div className="app-badge-text">
                <span className="badge-eyebrow">GET IT ON</span>
                <span className="badge-store-title">Google Play</span>
              </div>
            </a>
          </div>

        </div>
      </section>

      {/* 4. THE AUTHENTIC 3-STEP "HOW IT WORKS" ARCHITECTURE */}
      <section className="how-it-works-section" id="how-it-works">
        <div className="section-eyebrow">Simple 3-Step Setup</div>
        <h2 className="section-title">How It Works</h2>
        <p className="section-subtitle">
          No bank logins, no broken connection errors, and no complicated spreadsheets. You are 60 seconds away from knowing your exact safe spending number.
        </p>

        <div className="steps-grid">
          <div className="step-card step-1">
            <div className="step-number-badge">1</div>
            <h3>Set your Pay Days</h3>
            <p>
              Lock in your recurring income cadence—Weekly, Bi-Weekly, Semi-Monthly, or Monthly. Your entire runway organizes around when your money actually hits.
            </p>
          </div>

          <div className="step-card step-2">
            <div className="step-number-badge">2</div>
            <h3>Add your Accounts</h3>
            <p>
              Add your Checking, Cash Reserve, or Savings balances in seconds. Zero banking passwords or intrusive third-party data aggregators required.
            </p>
          </div>

          <div className="step-card step-3">
            <div className="step-number-badge">3</div>
            <h3>Add your Bills</h3>
            <p>
              Assign your rent, subscriptions, and utilities to the specific paychecks that cover them. Never get blindsided by due dates again.
            </p>
          </div>
        </div>

        <div className="steps-closing-banner">
          <CheckCircle2 size={22} color="#10B981" style={{ display: 'inline-block', verticalAlign: 'middle', marginRight: '8px' }} />
          <span>And Ledger Planner does the rest.</span>
        </div>
      </section>

      {/* 5. THE 6 CORE PILLARS */}
      <section className="features-section" id="features">
        <div className="section-eyebrow">Engineered For Certainty</div>
        <h2 className="section-title">Why Traditional Budget Apps Keep Failing You</h2>
        <p className="section-subtitle">
          Bank connections drop unexpectedly, third-party scrapers sell your data, and monthly budgets ignore when your paychecks actually hit.
        </p>

        <div className="features-grid">
          <div className="feature-card">
            <div className="feature-icon-wrapper green">
              <ShieldCheck size={28} />
            </div>
            <h3>100% Privacy-First Vault</h3>
            <p>
              Never link your bank credentials or tolerate broken OAuth feeds. Your financial history stays isolated, private, and unmonitored by aggregators.
            </p>
          </div>

          <div className="feature-card">
            <div className="feature-icon-wrapper blue">
              <CalendarDays size={28} />
            </div>
            <h3>Payday Routing Engine</h3>
            <p>
              Traditional apps lump all bills into a monthly bucket. Ledger Planner bridges each specific obligation to the exact paycheck that covers it.
            </p>
          </div>

          <div className="feature-card">
            <div className="feature-icon-wrapper orange">
              <Zap size={28} />
            </div>
            <h3>Autonomous Daily Briefings</h3>
            <p>
              Get daily proactive runway briefings. Know your exact safe-to-spend target each morning without logging into four separate banking portals.
            </p>
          </div>

          <div className="feature-card">
            <div className="feature-icon-wrapper blue">
              <Briefcase size={28} />
            </div>
            <h3>Creator & Agility Mode</h3>
            <p>
              Switch between standard W-2 paydays and irregular client invoices with a single tap. Always know your true business burn and tax buffer.
            </p>
          </div>

          <div className="feature-card">
            <div className="feature-icon-wrapper orange">
              <BellRing size={28} />
            </div>
            <h3>Subscription Redline Radar</h3>
            <p>
              Receive early-warning alerts 48 hours before recurring charges hit, keeping subscriptions from eating into your emergency safety reserves.
            </p>
          </div>

          <div className="feature-card">
            <div className="feature-icon-wrapper green">
              <Layers size={28} />
            </div>
            <h3>3-Second Tri-Color Clarity</h3>
            <p>
              Blue for fixed commitments, Green for paydays, and Orange for variable spending pacing. Know where you stand at a glance.
            </p>
          </div>
        </div>
      </section>

      {/* 6. THE COMPETITIVE MATRIX */}
      <section className="comparison-section">
        <div className="section-eyebrow">The Competitive Advantage</div>
        <h2 className="section-title">Built Different From The Ground Up</h2>
        <p className="section-subtitle">
          Compare Ledger Planner against traditional bank-sync scrapers and manual spreadsheets.
        </p>

        <div className="matrix-table-wrapper">
          <table className="matrix-table">
            <thead>
              <tr>
                <th>Capability</th>
                <th className="highlight">Ledger Planner 2.0</th>
                <th>Bank Sync Apps (Rocket, Copilot, Monarch)</th>
                <th>Traditional Tools (YNAB)</th>
                <th>Basic Trackers (BillOut, TimelyBills)</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td><strong>Bank Logins Required</strong></td>
                <td className="highlight"><span className="status-check"><Check size={16} /> Never (100% Vault)</span></td>
                <td><span className="status-cross"><X size={16} /> Mandatory (Plaid / MX)</span></td>
                <td><span className="status-cross"><X size={16} /> Mandatory or CSV</span></td>
                <td><span className="status-check"><Check size={16} /> Never</span></td>
              </tr>
              <tr>
                <td><strong>Broken Connection Downtime</strong></td>
                <td className="highlight"><span className="status-check"><Check size={16} /> 0% (Always Online)</span></td>
                <td><span className="status-cross"><X size={16} /> Frequent Sync Errors</span></td>
                <td><span className="status-cross"><X size={16} /> Frequent Sync Errors</span></td>
                <td><span className="status-check"><Check size={16} /> 0%</span></td>
              </tr>
              <tr>
                <td><strong>Autonomous AI Briefings</strong></td>
                <td className="highlight"><span className="status-check"><Check size={16} /> Twice-Daily (Gemini 3.8)</span></td>
                <td><span className="status-cross"><X size={16} /> None (Historical Only)</span></td>
                <td><span className="status-cross"><X size={16} /> None (Manual Rules)</span></td>
                <td><span className="status-cross"><X size={16} /> None (Simple Alerts)</span></td>
              </tr>
              <tr>
                <td><strong>Payday-to-Bill Automated Mapping</strong></td>
                <td className="highlight"><span className="status-check"><Check size={16} /> Built-in Engine (1–5 Paydays)</span></td>
                <td><span className="status-cross"><X size={16} /> Monthly Bucket Only</span></td>
                <td><span className="status-cross"><X size={16} /> Rigid Envelope Method</span></td>
                <td><span className="status-cross"><X size={16} /> Static Calendar Only</span></td>
              </tr>
              <tr>
                <td><strong>Creator & Agility Mode</strong></td>
                <td className="highlight"><span className="status-check"><Check size={16} /> One-Click Toggle</span></td>
                <td><span className="status-cross"><X size={16} /> Not Supported</span></td>
                <td><span className="status-cross"><X size={16} /> Not Supported</span></td>
                <td><span className="status-cross"><X size={16} /> Not Supported</span></td>
              </tr>
              <tr>
                <td><strong>Subscription Redline Radar</strong></td>
                <td className="highlight"><span className="status-check"><Check size={16} /> 48-Hour Proactive Radar</span></td>
                <td><span className="status-cross"><X size={16} /> Often Upsold</span></td>
                <td><span className="status-cross"><X size={16} /> Not Included</span></td>
                <td><span className="status-cross"><X size={16} /> Manual Entry Only</span></td>
              </tr>
              <tr>
                <td><strong>Annual Value Investment</strong></td>
                <td className="highlight"><span className="status-check">$49.99 / yr ($4.16/mo)</span></td>
                <td>$95 – $120+ / year</td>
                <td>$109+ / year</td>
                <td>Ad-supported or Basic</td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>

      {/* 7. STRATEGIC PAYWALL & PRICING */}
      <section className="pricing-section" id="pricing">
        <div className="section-eyebrow">Straightforward Investment</div>
        <h2 className="section-title">Simple, Transparent Pricing</h2>
        <p className="section-subtitle">
          Start your 14-day free trial today. Cancel anytime with a single click.
        </p>

        <div className="pricing-grid">
          <div className="price-card">
            <div>
              <div className="price-tier-name">Monthly Agile</div>
              <div className="price-tier-desc">Month-to-month cash flow clarity with zero long-term commitments.</div>
              <div className="price-tag">$4.99<span>/month</span></div>
              <ul className="price-features-list">
                <li><Check size={16} /> Full Payday Routing Engine</li>
                <li><Check size={16} /> Unlimited Bills & Income Sources</li>
                <li><Check size={16} /> Safe-To-Spend Daily Runway</li>
                <li><Check size={16} /> 100% Privacy Guarantee</li>
              </ul>
            </div>
            <a href={PLAN_MONTHLY_URL} className="btn-price-secondary">Start 14-Day Free Trial</a>
          </div>

          <div className="price-card featured">
            <span className="featured-pill">Most Popular • Save Over 20%</span>
            <div>
              <div className="price-tier-name">Annual Pro</div>
              <div className="price-tier-desc">Continuous runway intelligence for long-term clarity.</div>
              <div className="price-tag">$49.99<span>/year</span></div>
              <ul className="price-features-list">
                <li><Check size={16} /> Everything in Monthly Agile</li>
                <li><Check size={16} /> Equivalent to just $4.16/mo</li>
                <li><Check size={16} /> Priority AI Briefing Updates</li>
                <li><Check size={16} /> Creator & Agility Mode</li>
                <li><Check size={16} /> 48-Hour Subscription Radar</li>
                <li><Check size={16} /> Guaranteed Renewal Rate Lock</li>
              </ul>
            </div>
            <a href={PLAN_ANNUAL_URL} className="btn-price-primary">Start 14-Day Free Trial</a>
          </div>

          <div className="price-card">
            <div>
              <div className="price-tier-name">Founder Lifetime</div>
              <div className="price-tier-desc">One single upfront payment for permanent financial peace of mind.</div>
              <div className="price-tag">$99<span>one-time</span></div>
              <ul className="price-features-list">
                <li><Check size={16} /> Unlimited Lifetime System Access</li>
                <li><Check size={16} /> All Future Version 2.x Upgrades</li>
                <li><Check size={16} /> Dedicated VIP Founder Support</li>
                <li><Check size={16} /> Founder Badge in App</li>
              </ul>
            </div>
            <a href={PLAN_FOUNDER_URL} className="btn-price-secondary">Claim Lifetime Spot</a>
          </div>
        </div>
      </section>

      {/* 8. FAQ SECTION */}
      <section className="how-it-works-section" id="faq" style={{ paddingTop: '2rem' }}>
        <div className="section-eyebrow">Frequently Asked Questions</div>
        <h2 className="section-title">Answers to Common Questions</h2>
        <p className="section-subtitle">
          Everything you need to know about our privacy-first cash flow engine.
        </p>

        <div className="features-grid" style={{ marginTop: '2.5rem' }}>
          <div className="feature-card">
            <div className="feature-icon-wrapper green">
              <HelpCircle size={28} />
            </div>
            <h3>Why don’t you connect to my bank?</h3>
            <p>
              Traditional aggregators sell transaction data and frequently break credentials. With Ledger Planner, your data stays in your sovereign private vault—never tracked, never brokered.
            </p>
          </div>

          <div className="feature-card">
            <div className="feature-icon-wrapper blue">
              <HelpCircle size={28} />
            </div>
            <h3>How does payday mapping work?</h3>
            <p>
              Instead of an unrealistic calendar month budget, our engine anchors your bills to the exact paycheck cycle that covers them, keeping you ahead of every due date.
            </p>
          </div>

          <div className="feature-card">
            <div className="feature-icon-wrapper orange">
              <HelpCircle size={28} />
            </div>
            <h3>Can I cancel anytime?</h3>
            <p>
              Yes. You can manage or cancel your subscription at any time with a single click inside your billing settings with zero hassles.
            </p>
          </div>
        </div>
      </section>

      {/* 9. FINAL CONVERSION CLOSER */}
      <section className="closer-section">
        <div className="closer-box">
          <h2 className="closer-headline">Ready for Frictionless Financial Peace of Mind?</h2>
          <p className="closer-subtext">
            Join individuals and creators who master cash flow without surrendering bank logins or fighting broken syncs.
          </p>
          <div className="hero-cta-group">
            <div className="hero-actions-row">
              <a href={PLAN_ANNUAL_URL} className="btn-hero-primary">
                <span>Claim Your 14-Day Free Access</span>
                <ArrowRight size={20} />
              </a>
              <a href={DEMO_URL} className="btn-hero-demo">
                <span>Test Drive Sandbox Demo</span>
              </a>
            </div>
            <div className="cta-reassurance">
              <span><Lock size={14} /> Zero risk</span>
              <span>•</span>
              <span><Clock size={14} /> 60-second setup</span>
              <span>•</span>
              <span><Check size={14} /> No bank credentials</span>
            </div>
          </div>
        </div>
      </section>

      {/* 10. FOOTER */}
      <footer className="main-footer">
        <div className="footer-links">
          <a href={APP_URL}>App Login</a>
          <a href={DEMO_URL}>Interactive Demo</a>
          <a href="#how-it-works">How It Works</a>
          <a href="#features">Features</a>
          <a href="#pricing">Pricing Plans</a>
          <a href="#faq">FAQ</a>
          <a href="mailto:support@ledgerplanner.com">Contact Support</a>
        </div>
        <p>&copy; 2026 Ledger Planner. All rights reserved. Sovereign Financial Intelligence.</p>
      </footer>

    </div>
  );
}

export default App;
